<app-page size="wide">
    <div class="toolbar">
        <div class="date-control">
            <app-period-stepper (labelClick)="picker.open()" (next)="nextDay()" (previous)="previousDay()"
                                [label]="dateLabel()" nextLabel="Volgende dag" previousLabel="Vorige dag">
                <i class="fa-regular fa-calendar" icon></i>
            </app-period-stepper>
            <!-- Anchor for the calendar popup; the stepper label opens it -->
            <input (dateChange)="goToDate($event.value)" [matDatepicker]="picker" [value]="selectedDate" aria-hidden="true"
                   class="date-anchor" tabindex="-1">
            <mat-datepicker #picker></mat-datepicker>
        </div>
        <button (click)="today()" mat-stroked-button type="button">Vandaag</button>
        <div class="day-total">
            <span class="ui-meta">{{ totalLabel() }}</span>
            <span class="value">{{ totalTime() || '0:00' }}</span>
        </div>

        <div class="search">
            <mat-form-field [class.focused]="searchFocused()">
                <i class="fa-regular fa-magnifying-glass" matPrefix></i>
                <input (blur)="searchFocused.set(false)" (focus)="searchFocused.set(true)" (keydown)="searchKeydown($event)"
                       [formControl]="searchNewProjectText" aria-label="Taak toevoegen" autocomplete="off" matInput
                       placeholder="Taak toevoegen: zoek klant, project of taak…" type="text">
                @if (searchOpen() && searchResults().length) {
                    <span class="search-keys" matSuffix>↑↓ ↵</span>
                }
            </mat-form-field>
            @if (searchOpen()) {
                <div class="search-results" role="listbox">
                    @switch (searchState()) {
                        @case ('results') {
                            @for (group of searchGroups(); track group.client) {
                                <div class="group">
                                    <span [style.background]="group.companyId | customerColor" class="group-dot"></span>{{ group.client }}
                                </div>
                                @for (item of group.items; track item.row.project.id) {
                                    <div (mousedown)="pickSearchResult($event, item.row)" (mouseenter)="searchIndex.set(item.index)"
                                         [attr.aria-selected]="item.index === searchIndex()" [class.highlighted]="item.index === searchIndex()"
                                         [title]="item.row.title" class="result" role="option">
                                        <span class="result-text">
                                            <span class="result-task">@for (part of highlight(item.row.task); track $index) {<span [class.match]="part.match">{{ part.text }}</span>}</span>
                                            <span class="result-path">@for (part of highlight(item.row.sub); track $index) {<span [class.match]="part.match">{{ part.text }}</span>}</span>
                                        </span>
                                        @if (item.added) {
                                            <span class="added">Al in lijst</span>
                                        }
                                    </div>
                                }
                            }
                        }
                        @case ('empty') {
                            <div class="search-help">
                                Geen taken gevonden voor “{{ searchQuery() }}”.<br>Alleen user stories met een parent zijn zichtbaar.
                            </div>
                        }
                        @case ('busy') {
                            <div class="search-help">Zoeken…</div>
                        }
                        @default {
                            <div class="search-help">Typ minimaal 3 tekens om te zoeken op klant, project, nummer of taak.</div>
                        }
                    }
                </div>
            }
        </div>

        <div class="actions">
            <button (click)="reload()" [class.done]="reloadState() === 'done'" [disabled]="reloadState() === 'busy'" class="reload"
                    mat-stroked-button title="Gegevens opnieuw ophalen" type="button">
                @switch (reloadState()) {
                    @case ('busy') {
                        <i class="fa-regular fa-rotate-right fa-spin"></i> Herladen…
                    }
                    @case ('done') {
                        <i class="fa-regular fa-check"></i> Bijgewerkt
                    }
                    @default {
                        <i class="fa-regular fa-rotate-right"></i> Herladen
                    }
                }
            </button>
            <button (click)="continue()" class="continue" mat-stroked-button title="Laatste registratie doortrekken tot nu" type="button">
                <i class="fa-solid fa-play"></i> Doorgaan tot nu
            </button>
            <button (click)="save()" [class.saved]="saveLabel() === 'Opgeslagen'" class="save" mat-flat-button type="button">
                {{ saveLabel() }}<span class="shortcut">Ctrl S</span>
            </button>
        </div>
    </div>

    @if (showErrorMessage()) {
        <app-info-banner [title]="isFuture(selectedDate) ? 'Deze dag ligt in de toekomst.' : 'Deze dag is vergrendeld.'" icon="lock" tone="warn">
            @if (isFuture(selectedDate)) {
                Uren registreren kan pas op de dag zelf.
            } @else {
                Uren kun je alleen wijzigen vanaf {{ niceDate(earliestEditableDate(), true) }}. Opmerkingen kun je wel lezen.
            }
            <button (click)="today()" actions mat-stroked-button type="button">Naar vandaag</button>
        </app-info-banner>
    } @else if (!isToday(selectedDate)) {
        <app-info-banner [title]="'Je bekijkt ' + niceDate(selectedDate) + ', niet vandaag.'" tone="info">
            @if (isWeekend(selectedDate)) {
                Dit is een weekenddag.
            }
            Wijzigingen worden op deze dag opgeslagen.
            <button (click)="today()" actions mat-stroked-button type="button">Naar vandaag</button>
        </app-info-banner>
    } @else if (isWeekend(selectedDate)) {
        <app-info-banner icon="calendar" title="Vandaag is een weekenddag." tone="info"></app-info-banner>
    }

    <div class="grid-card">
        <div #registrationhours class="grid-scroll">
            <div [class.locked]="showErrorMessage()" class="grid">
                <div class="grid-row head">
                    <div class="cell task">Taak</div>
                    <div class="cell time">Tijd</div>
                    <div class="hours-head">
                        @for (hour of hourCells(); track hour.time) {
                            <div [class.now]="isNowHour(hour)" [class.off]="hour.off" class="hour">{{ hour.label }}</div>
                        }
                    </div>
                </div>
                @for (row of rows(); track row.project.id) {
                    <div (mouseenter)="projectHover(row.project.id)" (mouseleave)="projectLeave()" [class.hovered]="hoveredProject == row.project.id"
                         class="grid-row">
                        <div [title]="row.title" class="cell task">
                            <span [style.background]="row.companyId | customerColor:'tint'" [style.color]="row.companyId | customerColor:'ink'"
                                  class="tag">{{ row.client }}</span>
                            <span class="names">
                                <span class="name">{{ row.task }}</span>
                                <span class="sub">{{ row.sub }}</span>
                            </span>
                            @if (!totalTime(row.project.id)) {
                                <button (click)="removeVisible(row.project)" aria-label="Uit de lijst halen" class="hide" title="Uit de lijst halen"
                                        type="button">
                                    <i class="fa-regular fa-eye-slash"></i>
                                </button>
                            }
                        </div>
                        <div [class.empty]="!totalTime(row.project.id)" class="cell time">{{ totalTime(row.project.id) || '—' }}</div>
                        <div (mousedown)="lg($event)" (mousemove)="lg($event)" (mouseup)="lg($event)" class="hours">
                            @for (quarter of quarterCells(); track quarter.time) {
                                <div (contextmenu)="openCommentMenu($event, quarter, row)"
                                     [attr.data-quarter]="quarter.time"
                                     [attr.data-workproject]="row.project.id"
                                     [class.active]="cellIsActive(quarter, row)"
                                     [class.full]="quarter.hourStart"
                                     [class.hascomment]="cellHasNote(quarter, row)"
                                     [class.off]="quarter.off"
                                     [title]="cellTitle(quarter)"
                                     class="quarter">
                                </div>
                            }
                            @for (block of blocksFor(row.project.id); track block.from) {
                                <div [style.background]="row.companyId | customerColor" [style.left.%]="block.left" [style.width.%]="block.width"
                                     class="block"></div>
                            }
                            @if (nowLeft() !== null) {
                                <div [style.left.%]="nowLeft()" class="now-line"></div>
                            }
                        </div>
                    </div>
                } @empty {
                    <div class="grid-empty">Nog geen taken in de lijst. Zoek bovenaan een klant, project of taak om er een toe te voegen.</div>
                }
            </div>
        </div>
        <div class="grid-foot">Klik op een kwartier om het te registreren. Rode lijn = nu. Een taak toevoegen doe je via het zoekveld bovenaan.</div>
    </div>

    <div class="row g-3 align-items-start">
        <div class="col-12 col-lg-6">
            <app-section icon="keyboard" layout="stacked" title="Zo werkt het">
                <div class="help">
                    <div class="help-row">
                        <span class="keys"><app-kbd>Klik</app-kbd></span>
                        <span>Eén kwartier registreren of weghalen.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Shift</app-kbd><app-kbd>Klik</app-kbd></span>
                        <span>Registreren vanaf het laatst aangeklikte kwartier tot hier.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Ctrl</app-kbd><app-kbd>Klik</app-kbd></span>
                        <span>Een heel blok weghalen.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Doorgaan</app-kbd></span>
                        <span>Laatste registratie doortrekken tot nu.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Rechtsklik</app-kbd></span>
                        <span>Opmerking toevoegen. Laat leeg om te verwijderen.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Ctrl</app-kbd><app-kbd>S</app-kbd></span>
                        <span>Opslaan.</span>
                    </div>
                    <div class="help-row">
                        <span class="keys"><app-kbd>Zoeken</app-kbd></span>
                        <span>Taak toevoegen via het zoekveld. Pijltjes en Enter werken ook.</span>
                    </div>
                </div>
                <div class="ui-info-block mt-3"><b>Taak niet te vinden?</b> Alleen user stories met een parent zijn zichtbaar. Controleer beide.</div>
            </app-section>
        </div>
        <div class="col-12 col-lg-6">
            <app-section icon="comment" layout="stacked" title="Opmerkingen">
                @for (comment of commentList(); track comment.key) {
                    <button (click)="openCommentFromList($event, comment.key)" class="comment" type="button">
                        <span class="comment-meta">
                            <span class="comment-dot"></span>
                            <span class="font-monospace">{{ comment.label }}</span>
                            @if (comment.row) {
                                <span [style.background]="comment.row.companyId | customerColor:'tint'" [style.color]="comment.row.companyId | customerColor:'ink'"
                                      class="tag">{{ comment.row.client }}</span>
                                <span class="ui-ellipsis">{{ comment.row.task }}</span>
                            }
                        </span>
                        <span class="comment-text">{{ comment.value }}</span>
                    </button>
                } @empty {
                    <span class="ui-faint">Nog geen opmerkingen. Rechtsklik op een kwartier om er een toe te voegen.</span>
                }
            </app-section>
        </div>
    </div>
</app-page>

@if (showCommentModal) {
    <div (click)="closeCommentMenu()" (contextmenu)="closeCommentMenu()" class="comment-backdrop"></div>
    <div [style.left.px]="commentPosition.x" [style.top.px]="commentPosition.y" aria-label="Opmerking" class="comment-popover" role="dialog">
        <div class="popover-head">
            <span class="popover-title"><span class="comment-dot"></span>{{ commentTitle() }}</span>
            <span class="popover-sub">{{ commentSubtitle() }}</span>
        </div>
        <div class="popover-body">
            <mat-form-field>
                <textarea #comment (keydown)="commentKeydown($event)" [formControl]="tempComment" aria-label="Opmerking" matInput
                          placeholder="Wat wil je bij dit kwartier vastleggen?" rows="3"></textarea>
                <mat-hint>Leeg opslaan = opmerking verwijderen</mat-hint>
            </mat-form-field>
        </div>
        <div class="popover-foot">
            @if (commentQuarter && hasComment(commentQuarter.time)) {
                <button (click)="clearComment()" class="delete" mat-button type="button">Verwijderen</button>
            }
            <button (click)="closeCommentMenu()" class="ms-auto" mat-stroked-button type="button">Annuleren</button>
            <button (click)="setComment()" mat-flat-button type="button">Bevestigen<span class="shortcut">Ctrl ↵</span></button>
        </div>
    </div>
}
